import React, { useState } from 'react';
import styles from './LocationStep.module.scss';
import CampaignLocation from 'components/ECWCreateCampaign/ECWCampaignLocation';
import ErrorContainer from '../ErrorContainer';
import RadioButton from '../RadioButton';
import Select from '../Select';
import { StandaloneSearchBox } from '@react-google-maps/api';
import Input from '../Input';
import { GOOGLE_MAPS, POLYGON_TYPE } from 'constants/googleMaps';
import { ReactComponent as RemoveIcon } from '../../../assets/images/remove_circle_outline.svg';
import classNames from 'classnames';
import { useEffect } from 'react';
import { extractAddressAndCity } from 'utils/googleLocations';

const GEOFENCE_TYPE = {
  GUIDED: 'GUIDED',
  EXPERIENCED: 'EXPERIENCED',
};

const SELECT_OPTIONS = [
  {
    label: '0.1 mile',
    value: 0.1,
  },
  {
    label: '0.25 miles',
    value: 0.25,
  },
  {
    label: '0.5 miles',
    value: 0.5,
  },
  {
    label: '1.0 miles',
    value: 1,
  },
  {
    label: '5 miles',
    value: 5,
  },
  {
    label: '10 miles',
    value: 10,
  },
];

export const POLYGON_COLORS = [
  '#FF6B6B', // Soft Red
  '#4ECDC4', // Aqua Green
  '#FFE66D', // Light Yellow
  '#1A535C', // Deep Teal
  '#FF9F1C', // Orange
  '#6A0572', // Purple
  '#3D348B', // Indigo
  '#00A896', // Sea Green
  '#E63946', // Crimson
  '#F4A261', // Peach
  '#2A9D8F', // Cool Teal
  '#A8DADC', // Pale Blue
  '#F72585', // Hot Pink
  '#7209B7', // Vivid Purple
  '#457B9D', // Steel Blue
  '#06D6A0', // Mint
  '#F3722C', // Burnt Orange
  '#118AB2', // Sky Blue
  '#EF476F', // Rose Red
  '#073B4C', // Navy Teal
  '#9B5DE5', // Electric Violet
  '#00F5D4', // Neon Aqua
  '#F15BB5', // Bright Pink
  '#2EC4B6', // Aqua Mint
  '#FF6F91', // Coral Pink
  '#6FFFE9', // Pale Aqua
  '#5C7AFF', // Periwinkle Blue
  '#F9C80E', // Golden Yellow
  '#E56B6F', // Rose Clay
  '#5C5470', // Gray Violet
];

const LocationStep = ({
  setValue,
  setPolygons,
  polygons,
  errors,
  importedCampaign,
  selectedGeofences,
  setSelectedGeofences,
}) => {
  const [geofenceType, setGeofenceType] = useState(GEOFENCE_TYPE.GUIDED);
  const [type, setType] = useState(GOOGLE_MAPS.LOCATION_TYPE_COUNTRY);
  const [location, setLocation] = useState(null);
  const [geofences, setGeofences] = useState([
    {
      radius: {
        label: '0.25 miles',
        value: 0.25,
      },
    },
  ]);
  const [refs, setRefs] = useState([]);

  useEffect(() => {
    if (importedCampaign?.locations) {
      const newGeofences = [];
      for (const locataion of importedCampaign.locations) {
        const foundRadius = SELECT_OPTIONS.find(
          (r) => r.value === locataion.path[0].radius
        );

        const newRadius = foundRadius || {
          label: '0.25 miles',
          value: 0.25,
        };

        newGeofences.push({
          radius: newRadius,
          value: `${locataion.address}, ${locataion.city}`,
        });
      }
      setGeofences(newGeofences);
    }
  }, [importedCampaign]);

  const onLoad = (ref, index) => {
    const newRefs = [...refs];
    newRefs[index] = ref;
    setRefs(newRefs);
  };

  const onPlacesChanged = (index) => {
    if (refs[index].getPlaces()[0].address_components) {
      const lat = refs[index].getPlaces()[0].geometry.location.lat();
      const lng = refs[index].getPlaces()[0].geometry.location.lng();
      setLocation({ lat, lng });
      setType(refs[index].getPlaces()[0].types[0]);
      const { city, address } = extractAddressAndCity(
        refs[index].getPlaces()[0].address_components
      );

      const newSelectedGeofences = [...selectedGeofences];
      newSelectedGeofences[index] = {
        city,
        address,
        centerPoint: { latitude: lat, longitude: lng },
        path: [
          {
            radius: geofences[index].radius.value,
            latitude: lat,
            longitude: lng,
          },
        ],
      };
      setSelectedGeofences(newSelectedGeofences);

      const radius = geofences[index].radius.value * 1609.34;
      const newPolygons = [...polygons];
      newPolygons[index] = {
        id: Date.now(),
        type: POLYGON_TYPE.CIRCLE,
        radius,
        centerPoint: { lat, lng },
        color: POLYGON_COLORS[index],
      };
      setPolygons(newPolygons);
    }
  };

  const onAddAnotherLocation = () => {
    setGeofences((prev) => [
      ...prev,
      {
        radius: {
          label: '0.25 miles',
          value: 0.25,
        },
      },
    ]);
  };

  return (
    <div className={styles.container}>
      <div className={styles.formRow}>
        <div className={styles.leftContainer}>
          <p className={styles.textLeft}>Enter your geofence locations...</p>
        </div>
        <div className={styles.radioButtonContainer}>
          <RadioButton
            title="Basic: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Address + Radius"
            checked={geofenceType === GEOFENCE_TYPE.GUIDED}
            onClick={() => {
              setRefs([]);
              setPolygons([]);
              setGeofences([
                {
                  radius: {
                    label: '0.25 miles',
                    value: 0.25,
                  },
                },
              ]);
              setLocation(null);
              setType(GOOGLE_MAPS.LOCATION_TYPE_COUNTRY);
              setGeofenceType(GEOFENCE_TYPE.GUIDED);
            }}
          />
          <RadioButton
            title="Advanced:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Draw Geofences"
            checked={geofenceType === GEOFENCE_TYPE.EXPERIENCED}
            onClick={() => {
              setRefs([]);
              setPolygons([]);
              setGeofences([
                {
                  radius: {
                    label: '0.25 miles',
                    value: 0.25,
                  },
                },
              ]);
              setLocation(null);
              setType(GOOGLE_MAPS.LOCATION_TYPE_COUNTRY);
              setGeofenceType(GEOFENCE_TYPE.EXPERIENCED);
            }}
          />
        </div>
      </div>
      {geofenceType === GEOFENCE_TYPE.GUIDED && (
        <div>
          {geofences.map((g, index) => {
            return (
              <div className={styles.formRow} key={index}>
                <div className={styles.inputsContainer}>
                  <div
                    className={classNames(styles.square)}
                    style={{
                      background: POLYGON_COLORS[index],
                    }}
                  />
                  <StandaloneSearchBox
                    onLoad={(ref) => onLoad(ref, index)}
                    onPlacesChanged={() => onPlacesChanged(index)}
                  >
                    <Input
                      label={`Location ${index + 1}:`}
                      placeholder="Enter location"
                      value={g.value}
                      hideErrorMessage
                      className={styles.input}
                    />
                  </StandaloneSearchBox>
                  <div className={styles.selectContainer}>
                    <label className={styles.label}>Radius (miles)</label>
                    <Select
                      options={SELECT_OPTIONS}
                      selectedOption={g.radius}
                      handleChange={(value) => {
                        const arr = [...geofences];
                        arr[index] = { radius: value };
                        setGeofences(arr);
                        const newSelectedGeofences = [...selectedGeofences];
                        newSelectedGeofences[index] = {
                          ...newSelectedGeofences[index],
                          radius: value.value,
                        };
                        setSelectedGeofences(newSelectedGeofences);
                        if (polygons.length) {
                          const polygonsArr = [...polygons];

                          polygonsArr[index] = {
                            ...polygonsArr[index],
                            radius: value.value * 1609.34,
                          };
                          setPolygons(polygonsArr);
                        }
                      }}
                    />
                  </div>
                  {index > 0 && (
                    <div
                      className={styles.removeIconWrapper}
                      onClick={() => {
                        setPolygons((prev) =>
                          prev.filter((_, i) => i !== index)
                        );
                        setGeofences((prev) =>
                          prev.filter((_, i) => i !== index)
                        );
                      }}
                    >
                      <RemoveIcon />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div
            className={styles.addAnotherLocation}
            onClick={onAddAnotherLocation}
          >
            + add location
          </div>
        </div>
      )}
      <CampaignLocation
        polygons={polygons}
        setPolygons={setPolygons}
        setValue={setValue}
        externalType={type}
        externalLocation={location}
      />
      <ErrorContainer errorText={errors.hasMapLocation?.message} />
    </div>
  );
};

export default LocationStep;
